import { CardStackIcon, CheckIcon } from "@radix-ui/react-icons";
import { ChartBarIcon, TableIcon } from "@heroicons/react/outline";
import {
  ChartBarIcon as ChartBarIconSolid,
  TableIcon as TableIconSolid,
} from "@heroicons/react/solid";
import { ChevronRight } from "lucide-react";

import { Button, type ButtonProps } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import { cn } from "@/lib/utils";

const activeDropdownItemClassName =
  "bg-blue-100 font-normal text-blue-700 hover:bg-blue-200 focus:bg-blue-200 focus:text-blue-700";

interface RequestsNavButtonMenuProps {
  isCollapsed: boolean;
  submenuOpen: boolean;
  onSubmenuOpenChange: (open: boolean) => void;
  onSubmenuOpen: () => void;
  onSubmenuClose: () => void;
  buttonVariant: ButtonProps["variant"];
  caretRotationClass: string;
  isRequestsSectionActive: boolean;
  showObservabilityCaret: boolean;
  isRequestLogsPageActive: boolean;
  isAnalyticsActive: boolean;
  onPrimaryClick: () => void;
  onRequestLogsClick: () => void;
  onAnalyticsClick: () => void;
}

export const RequestsNavButtonMenu = ({
  isCollapsed,
  submenuOpen,
  onSubmenuOpenChange,
  onSubmenuOpen,
  onSubmenuClose,
  buttonVariant,
  caretRotationClass,
  isRequestsSectionActive,
  showObservabilityCaret,
  isRequestLogsPageActive,
  isAnalyticsActive,
  onPrimaryClick,
  onRequestLogsClick,
  onAnalyticsClick,
}: RequestsNavButtonMenuProps) => (
  <div className={cn("w-full", !isCollapsed && "px-2", isCollapsed && "flex justify-center")}>
    <DropdownMenu open={submenuOpen} onOpenChange={onSubmenuOpenChange} modal={false}>
      <DropdownMenuTrigger asChild>
        <div
          className={cn(isCollapsed ? "flex justify-center" : "w-full")}
          onMouseEnter={onSubmenuOpen}
          onMouseLeave={onSubmenuClose}
        >
          <Button
            variant={buttonVariant}
            size={isCollapsed ? "icon" : "default"}
            className={cn(
              "text-sm font-medium",
              isCollapsed ? "h-10 w-10 justify-center p-0" : "h-9 w-full justify-start gap-2 pl-3",
              submenuOpen && !isRequestsSectionActive && "bg-accent",
            )}
            onClick={onPrimaryClick}
            onPointerDown={event => event.preventDefault()}
          >
            <CardStackIcon className={isCollapsed ? "h-5 w-5" : "h-4 w-4"} />
            {!isCollapsed ? <span>Requests</span> : null}
            {!isCollapsed && showObservabilityCaret ? (
              <ChevronRight
                className={cn("ml-auto h-4 w-4 transition-transform", caretRotationClass)}
              />
            ) : null}
          </Button>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="right"
        align="start"
        sideOffset={8}
        className="z-[100] min-w-[10rem] p-1"
        onMouseEnter={onSubmenuOpen}
        onMouseLeave={onSubmenuClose}
        onCloseAutoFocus={event => event.preventDefault()}
      >
        <DropdownMenuItem
          className={cn(
            "gap-2 font-normal",
            isRequestLogsPageActive && activeDropdownItemClassName,
          )}
          onSelect={event => {
            event.preventDefault();
            onRequestLogsClick();
          }}
        >
          {isRequestLogsPageActive ? (
            <TableIconSolid className="h-4 w-4" />
          ) : (
            <TableIcon className="h-4 w-4" />
          )}
          <span className="flex-1">Requests Table</span>
          {isRequestLogsPageActive ? (
            <CheckIcon className="size-4 flex-shrink-0 text-blue-600" />
          ) : null}
        </DropdownMenuItem>
        <DropdownMenuItem
          className={cn("gap-2 font-normal", isAnalyticsActive && activeDropdownItemClassName)}
          onSelect={event => {
            event.preventDefault();
            onAnalyticsClick();
          }}
        >
          {isAnalyticsActive ? (
            <ChartBarIconSolid className="h-4 w-4" />
          ) : (
            <ChartBarIcon className="h-4 w-4" />
          )}
          <span className="flex-1">Analytics</span>
          {isAnalyticsActive ? <CheckIcon className="size-4 flex-shrink-0 text-blue-600" /> : null}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
);
